/** Low galvanized-capped panel that closes the boundary between farrowing bays. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';
import { getZone, type ZonePalette } from '@/render/palette';

const zone: ZonePalette = (() => {
  const found = getZone('farrowing');
  if (!found) throw new Error('farrowing zone missing from the palette');
  return found;
})();

function pipe(radius: number, length: number, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, length, 8, 1), surface('metal', 0xaeb7b9));
  mesh.name = name;
  mesh.castShadow = true;
  return mesh;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'pen-partition';

  // The panel reaches the ground datum even though a dressed bay buries its
  // lower 0.09 m in slats. That keeps this independently placeable kit part
  // honest, just as the pen rail's uprights remain whole below the kerb.
  const panel = new THREE.Mesh(
    new THREE.BoxGeometry(0.05, 0.58, 3.16),
    surface('mineral', zone.surfaces[2] ?? 0x8d918c),
  );
  panel.name = 'panel';
  panel.position.y = 0.29;
  panel.castShadow = true;
  group.add(panel);

  const cap = pipe(0.03, 3.16, 'cap');
  cap.rotation.x = Math.PI / 2;
  cap.position.y = 0.62;
  group.add(cap);

  for (const [z, name] of [[-1.55, 'post-near'], [1.55, 'post-far']] as const) {
    const post = pipe(0.025, 0.65, name);
    post.position.set(0, 0.325, z);
    group.add(post);
  }

  return group;
}

export const penPartitionAsset: AssetDefinition = {
  id: 'pen-partition',
  label: 'Pen partition',
  description: 'Low concrete bay panel with a galvanized capping pipe.',
  footprint: { width: 0.06, height: 0.65, length: 3.16 },
  create: build,
};
